import { Injectable } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AlertController, IonLoading, LoadingController, ModalController, NavController, Platform, PopoverController, ToastController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { I18nService } from '../i18n/i18n.service';
import { ApiProvider } from './api.provider';
import { AuthService } from './auth/auth.service';
import { StorageService } from './local-data/storage.service';
import { MatchVideosService } from './match-videos/match-videos.service';
import { VersionService } from './version/version.service';

export async function fetchTimeout(input: string | URL | globalThis.Request, init?: RequestInit, timeout: number = 3000): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort('Request timeout'), timeout);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * The hub every page reaches for: Ionic controllers, domain services, and the
 * toast/loading helpers.
 *
 * Services take `CoreProvider` through `initCore()` rather than constructor
 * injection — that is what keeps the graph acyclic when a service needs the hub
 * and the hub needs the service.
 */
@Injectable({
  providedIn: 'root',
})
export class CoreProvider {
  public readonly appIsReady$ = new BehaviorSubject<boolean | 'error'>(false);

  constructor(
    public readonly meta: Meta,
    public readonly navCtrl: NavController,
    public readonly toastCtrl: ToastController,
    public readonly alertCtrl: AlertController,
    public readonly modalCtrl: ModalController,
    public readonly popoverCtrl: PopoverController,
    public readonly loadingCtrl: LoadingController,
    public readonly router: Router,
    public readonly platform: Platform,
    public readonly i18n: I18nService,
    public readonly translate: TranslateService,
    public readonly storage: StorageService,
    public readonly api: ApiProvider,
    public readonly auth: AuthService,
    public readonly matchVideos: MatchVideosService,
    public readonly versionService: VersionService,
  ) {
    if (!environment.production && window) (window as unknown as { core: CoreProvider })['core'] = this;
    void this.init();
  }

  /** The running version, for display. `versionService` owns everything else about it. */
  public get version() {
    return this.versionService.current;
  }

  async init() {
    try {
      // First, and before anything that can raise a toast. `appIsReady$` gates
      // the whole shell, so having the locale file in hand by the time it flips
      // is what stops the app rendering a frame of English on its way to German.
      await this.i18n.init();
      await this.storage.initCore(this);
      this.api.setCore(this);
      await this.auth.initCore(this);
      await this.matchVideos.initCore(this);
      this.versionService.initCore(this);

      this.appIsReady$.next(true);
    } catch (error) {
      console.error('Error initializing app', error);
      this.appIsReady$.next('error');
    }
  }

  public getStripeReturnUrl(_success: boolean) {
    return window.location.origin + '/configuration/user-profile';
  }

  public openExternalUrl(url: string, target: '_blank' | '_self' = '_blank') {
    window.open(url, target);
  }

  //#region Feedback

  /**
   * The defaults below are where most of this app's feedback text lives, so
   * translating them here covers every call site that passes nothing — which is
   * the majority of them. A caller that does pass a message translates its own.
   *
   * `instant` and not the `tg` promise: these run inside `await create({...})`,
   * and the locale file is already loaded by the time anything can raise a
   * toast — `CoreProvider.init()` awaits `i18n.init()` before anything else.
   */
  public async genericLoading(message?: string, duration: number | null = 30000) {
    const loading = await this.loadingCtrl.create({
      message: message || this.translate.instant(_('Loading...')),
      mode: 'ios',
      duration: duration ?? undefined,
      spinner: 'lines-small',
    });
    await loading.present();
    return loading;
  }

  public async successToast(loading?: HTMLIonLoadingElement | LoadingStatusHandler, message?: string) {
    if (loading) {
      if ('dismiss' in loading && typeof loading.dismiss == 'function') void loading.dismiss();
      if (loading instanceof LoadingStatusHandler) loading.setSuccess();
    }

    while (await this.toastCtrl.getTop()) {
      await this.toastCtrl.dismiss();
    }

    await (
      await this.toastCtrl.create({
        message: message || this.translate.instant(_('Done')),
        duration: 3000,
        color: 'success',
        position: 'bottom',
        // The icon is what says how it went — the panel itself stays neutral.
        icon: 'checkmark-circle',
      })
    ).present();
  }

  public async errorToast(
    loading?: HTMLIonLoadingElement | LoadingStatusHandler,
    message: any | string = null,
    duration: number | null = null,
  ) {
    if (loading) {
      if ('dismiss' in loading && typeof loading.dismiss == 'function') void loading.dismiss();
      if (loading instanceof LoadingStatusHandler) loading.setError();
    }

    if (typeof message != 'string' && message != null) {
      message = message instanceof Error ? message.message : this.fetchErrMsg(message.error);
    }

    while (await this.toastCtrl.getTop()) {
      await this.toastCtrl.dismiss();
    }

    return await (
      await this.toastCtrl.create({
        message: message || this.translate.instant(_('Something went wrong, please try again later')),
        duration: duration || 10000,
        color: 'danger',
        position: 'bottom',
        icon: 'alert-circle',
        buttons: [{ text: this.translate.instant(_('OK')), role: 'cancel' }],
      })
    ).present();
  }

  public fetchErrMsg(err: any) {
    if (typeof err == 'string') {
      try {
        err = JSON.parse(err);
      } catch {
        return err;
      }
    }

    if (err.message?.[0]?.constraints)
      return err.message.reduce(
        (acc: string, itm: any) =>
          acc + '\n' + Object.keys(itm.constraints).reduce((acc2: string, key: string) => acc2 + '\n - ' + itm.constraints[key], ''),
        '',
      );
    if (err.error && err.message) return `${err.error}: ${err.message}`;
    if (err.message) return err.message;
    if (err.error) return err.error;
    if (err.errors) {
      let msg = this.translate.instant(_('The following errors occurred:')) + ' ';
      for (const itm in err.errors) {
        msg += '\n- ' + err.errors[itm].toString();
      }
      return msg;
    }
    if (Object.getPrototypeOf(err).toString().indexOf('ProgressEvent'))
      return this.translate.instant(_('A connection error occurred, please check your network.'));
    return err.toString();
  }

  //#endregion
}

export type LoadingStatusType = 'error' | 'loading' | 'success' | 'idle';
export class LoadingStatusHandler extends BehaviorSubject<LoadingStatusType> {
  timeout: any;
  constructor(initial: LoadingStatusType = 'idle') {
    super(initial);
  }
  override next(_value: LoadingStatusType): void {
    throw new Error('Setting status directly is not allowed');
  }
  setIdle() {
    super.next('idle');
    this.clearTimeout();
  }
  setLoading() {
    super.next('loading');
    this.clearTimeout();
  }
  setError() {
    super.next('error');
    this.clearTimeout();
  }
  setSuccess() {
    super.next('success');
    this.clearTimeout();
    this.timeout = setTimeout(() => this.setIdle(), 3000);
  }

  private clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }
}

export type { IonLoading };
